import type { ConnectMe, MailProvider, SuggestedProvider } from "@don/wire";

/**
 * The Connections page's whole conversation with the api (issue 392, PRD A §3): spend the
 * Connect link once, then read `GET /connect/me` with the `employee_session` cookie that spend
 * set. Only `/api/connect/*` is ever asked: no owner read, no error report (design-spec §8 W1,
 * W9). A port, so the state rules below are tested without a browser.
 */
export interface ConnectPort {
  /** `POST /connect/session`: `"ok"` on 204, `"refused"` on any refusal. Throws otherwise. */
  spend(token: string): Promise<"ok" | "refused">;
  /** `GET /connect/me`: the person, or `"signed-out"` when no live session answered. */
  me(): Promise<ConnectMe | "signed-out">;
  /**
   * Hand the tab to Google (issue 394): navigate to `GET /connect/google/start`, which stores
   * `state` and the PKCE verifier on the session row and redirects to the consent screen. A
   * top-level navigation, so the `SameSite=Lax` cookie rides it and Google's redirect back.
   * `chooseAccount` ("Use another account", issue 395) asks Google for its account chooser.
   */
  startGoogle(options?: { chooseAccount?: boolean }): void;
  /**
   * Hand the tab to Microsoft (issue 462): navigate to `GET /connect/microsoft/start`, as
   * {@link ConnectPort.startGoogle} does for Google.
   */
  startMicrosoft(options?: { chooseAccount?: boolean }): void;
  /**
   * `POST /connect/<provider>/disconnect` (issue 397; Outlook's route issue 469), no body:
   * `"ok"` on success, `"signed-out"` when no live session answered, `"failed"` for anything
   * else (the provider or the api down). Absent `provider` is Gmail.
   */
  disconnect(provider?: MailProvider): Promise<"ok" | "signed-out" | "failed">;
}

/** 401 and 404 both mean "no live session or link" to this page (design-spec §5.1, issue 392). */
const signedOut = (status: number) => status === 401 || status === 403 || status === 404;

export function connectPort(
  fetchFn: typeof fetch,
  base = "/api",
  navigate: (url: string) => void = (url) => window.location.assign(url),
): ConnectPort {
  return {
    startGoogle(options) {
      navigate(`${base}/connect/google/start${options?.chooseAccount === true ? "?account=choose" : ""}`);
    },
    startMicrosoft(options) {
      navigate(`${base}/connect/microsoft/start${options?.chooseAccount === true ? "?account=choose" : ""}`);
    },
    async spend(token) {
      const response = await fetchFn(`${base}/connect/session`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (response.ok) return "ok";
      if (signedOut(response.status)) return "refused";
      throw new Error(`connect session ${response.status}`);
    },
    async disconnect(provider = "google") {
      try {
        const response = await fetchFn(`${base}/connect/${provider}/disconnect`, { method: "POST", credentials: "same-origin" });
        if (response.ok) return "ok";
        return signedOut(response.status) ? "signed-out" : "failed";
      } catch {
        return "failed";
      }
    },
    async me() {
      const response = await fetchFn(`${base}/connect/me`, { credentials: "same-origin" });
      if (signedOut(response.status)) return "signed-out";
      if (!response.ok) throw new Error(`connect me ${response.status}`);
      return (await response.json()) as ConnectMe;
    },
  };
}

/**
 * Take the Connect link's token out of the URL's `#t=` fragment and strip the fragment with
 * `replaceState` **before** anything is sent (design-spec §5.5 D3), so neither history nor a
 * later screen holds it. `null` when the URL carries none. Pure over the `location` and
 * `history` it is handed.
 */
export function takeToken(
  location: Pick<Location, "hash" | "pathname" | "search">,
  history: Pick<History, "replaceState">,
): string | null {
  if (location.hash === "") return null;
  const token = new URLSearchParams(location.hash.slice(1)).get("t");
  history.replaceState(null, "", `${location.pathname}${location.search}`);
  return token === null || token === "" ? null : token;
}

/**
 * The api sends a Connect Gmail or Reconnect pressed on a dead session to `/connect#timed-out`
 * (`connect_google_start` in `crates/backend/src/lib.rs`, epic UX round 1 P1-3). Read before
 * {@link takeToken} strips the fragment.
 */
export function timedOut(location: Pick<Location, "hash">): boolean {
  return location.hash === "#timed-out";
}

/** What the page shows (design-spec §5.1, the states this issue builds). */
export type PageState =
  | { readonly kind: "opening" }
  | { readonly kind: "link-expired" }
  | { readonly kind: "session-expired" }
  | { readonly kind: "failed" }
  /**
   * `handoff`: Connect Gmail, or a problem's action, was pressed and the tab is on its way to
   * Google (issue 394); `"choose"` when that action was "Use another account" (issue 395).
   */
  | {
      readonly kind: "ready";
      readonly me: ConnectMe;
      readonly handoff?: boolean | "choose";
      /** Which provider's row was pressed for the handoff (issue 462); absent is Gmail. */
      readonly via?: MailProvider;
      /**
       * Disconnect (issue 397, design-spec §5.1, §5.4 W-dis): the confirm is open, its POST is in
       * flight, or the one-shot result of the last one. Never stored: a reload reads plain.
       */
      readonly disconnect?: Disconnect;
      /** The provider a `done` Disconnect removed (issue 469); absent is Gmail. */
      readonly disconnected?: MailProvider;
    };

export type Disconnect = "confirm" | "pending" | "done" | "failed";

/**
 * The confirmed Disconnect: the POST to the held Connection's provider (issue 469), then a fresh
 * `/me` (design-spec §5.5 D5). A failure keeps `me` as it was (the row stays connected); no live
 * session is session expired. A `done` names the provider removed when it is not Gmail.
 */
export async function disconnectConnection(port: ConnectPort, me: ConnectMe): Promise<PageState> {
  const provider = me.connection === null ? "google" : providerOf(me.connection);
  const answer = await port.disconnect(provider);
  if (answer === "signed-out") return { kind: "session-expired" };
  if (answer === "failed") return { kind: "ready", me, disconnect: "failed" };
  const done = provider === "google" ? {} : { disconnected: provider };
  try {
    const next = await port.me();
    if (next === "signed-out") return { kind: "session-expired" };
    return { kind: "ready", me: next, disconnect: "done", ...done };
  } catch {
    return { kind: "ready", me: { ...me, connection: null }, disconnect: "done", ...done };
  }
}

/** Epic A's name for {@link disconnectConnection} (issue 397's tests read it). */
export const disconnectGmail = disconnectConnection;

/**
 * The first load. With a token: spend it; a refusal is link expired; after a spend, a `/me`
 * with no live session is session expired (a later request, ux-spec 13). With no token: a
 * `/me` with no live session is link expired (§5.5 D4: a bookmarked `/connect` gets the way
 * back), unless the api sent the tab here `timedOut` ({@link timedOut}), which is session
 * expired. Anything thrown is `failed`.
 */
export async function firstLoad(port: ConnectPort, token: string | null, timedOut = false): Promise<PageState> {
  try {
    if (token !== null && (await port.spend(token)) === "refused") return { kind: "link-expired" };
    const me = await port.me();
    if (me === "signed-out") return { kind: token === null && !timedOut ? "link-expired" : "session-expired" };
    return { kind: "ready", me };
  } catch {
    return { kind: "failed" };
  }
}

/**
 * The providers the page offers rows for (issue 462): `providers` from `/connect/me`, Gmail
 * alone when absent (an api older than issue 462, or no Microsoft client deployed), Gmail then
 * Outlook. {@link layout} orders them by the guess.
 */
export function offered(me: ConnectMe): MailProvider[] {
  const providers = me.providers ?? ["google"];
  return (["google", "microsoft"] as const).filter((p) => providers.includes(p));
}

/** The provider a guess names: `microsoft_personal` is Outlook's. */
const guessed = (guess: SuggestedProvider): MailProvider => (guess === "google" ? "google" : "microsoft");

/**
 * The not-connected rows in order, the one primary and the guess (issue 464, Outlook design-spec
 * §5.1, W3): read from `suggested_provider` on every load and nothing else, so a Disconnect never
 * reorders them. The guessed row comes first with the primary; with no guess, Gmail then Outlook
 * and no primary. One row offered is epic A's page: its Connect is the primary and no guess
 * shows.
 */
export function layout(me: ConnectMe): { rows: MailProvider[]; primary: MailProvider | null; guess: SuggestedProvider | null } {
  const providers = offered(me);
  if (providers.length === 1) return { rows: providers, primary: providers[0]!, guess: null };
  // An admin state puts Outlook first whatever the guess (D5); its row draws its own actions.
  if (adminState(me) !== null) {
    return { rows: ["microsoft", ...providers.filter((p) => p !== "microsoft")], primary: null, guess: null };
  }
  const guess = me.suggested_provider ?? null;
  if (guess === null || !providers.includes(guessed(guess))) return { rows: providers, primary: null, guess: null };
  const first = guessed(guess);
  return { rows: [first, ...providers.filter((p) => p !== first)], primary: first, guess };
}

/**
 * A Connection's provider, Gmail when the answer carries none (an api older than issue 461, as
 * `wire::ConnectionView`'s serde default reads it).
 */
export function providerOf(connection: ConnectMe["connection"] & object): MailProvider {
  return (connection.provider as MailProvider | undefined) ?? "google";
}

/**
 * The provider a callback's outcome names (issue 463): the row it draws under. Gmail when the
 * answer carries none (an api older than issue 463, as `wire::LastAttempt`'s serde default reads it).
 */
export function attemptProviderOf(attempt: NonNullable<ConnectMe["last_attempt"]>): MailProvider {
  return (attempt.provider as MailProvider | undefined) ?? "google";
}

/**
 * The Outlook row's admin state (issue 465, Outlook design-spec §5.1, decisions 20, 23, 28):
 * `required` on the `admin_required` flash (shown once), `scopes` on a Microsoft `scopes_missing`
 * flash (D9), `blocked` on a `blocked` flash (issue 468: sign-in rules refused the connect),
 * `awaiting` from the stored `admin_wait` when no Microsoft flash shows, or `null`.
 * Only with no Connection and Outlook offered.
 */
export type AdminState = "required" | "scopes" | "awaiting" | "blocked";

export function adminState(me: ConnectMe): AdminState | null {
  if (me.connection !== null || !offered(me).includes("microsoft")) return null;
  const attempt = me.last_attempt;
  if (attempt !== null && attemptProviderOf(attempt) === "microsoft") {
    if (attempt.outcome === "admin_required") return "required";
    if (attempt.outcome === "scopes_missing") return "scopes";
    if (attempt.outcome === "blocked") return "blocked";
    // Another Microsoft outcome shows itself this once; the stored wait shows on the next load.
    return null;
  }
  return me.admin_wait !== undefined && me.admin_wait !== null ? "awaiting" : null;
}

/**
 * Whether the Outlook row draws the closed disclosure before an attempt (§5.1, decision 33): a
 * work guess or no guess, never a Gmail or personal guess.
 */
export function beforeAttemptDisclosure(me: ConnectMe): boolean {
  const guess = me.suggested_provider ?? null;
  return guess === null || guess === "microsoft";
}
